import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NotificationProvider } from "@/components/notifications/NotificationContext";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { DashboardLayoutRoute } from "@/components/DashboardLayout";
import nannukIcon from "@/assets/nannuk-icon.png";

import { pageImports, prefetchHotRoutes } from "@/lib/page-imports";

const Auth = lazy(pageImports.Auth);
const Overview = lazy(pageImports.Overview);
const Dashboard = lazy(pageImports.Dashboard);
const Movements = lazy(pageImports.Movements);
const Orders = lazy(pageImports.Orders);
const UsersAndClients = lazy(pageImports.UsersAndClients);
const Invoices = lazy(pageImports.Invoices);
const Profile = lazy(pageImports.Profile);
const CheckInvoices = lazy(pageImports.CheckInvoices);
const Settings = lazy(pageImports.Settings);
const Administration = lazy(pageImports.Administration);
const ClientsHub = lazy(pageImports.ClientsHub);
const ClientDetail = lazy(pageImports.ClientDetail);
const Amazon = lazy(pageImports.Amazon);
const FacturaPro = lazy(pageImports.FacturaPro);
const NotFound = lazy(pageImports.NotFound);

// Precarga solo las 4 rutas más usadas tras first paint. Las demás se
// prefetchean on-hover desde el sidebar (ver DashboardLayout).
prefetchHotRoutes();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000,     // data fresca 10 min → no refetch al remontar
      gcTime: 60 * 60 * 1000,         // cache en memoria 1 h
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

// NOTA: la persistencia del cache en sessionStorage se eliminó porque
// JSON.stringify no sabe serializar Map/Set (useAmazonOrdersMap devuelve
// un Map). Al rehidratar, los consumidores llamaban .has() sobre un
// objeto plano y crasheaba toda la app. El cache en memoria de React
// Query (staleTime 10 min + gcTime 1 h) ya cubre la navegación dentro
// de una misma sesión, que es lo que importa para la percepción.
if (typeof window !== "undefined") {
  try { sessionStorage.removeItem("nannuk-rq-cache-v1"); } catch { /* ignore */ }
}

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <img src={nannukIcon} alt="Nannuk" className="h-16 w-16 mx-auto mb-4 animate-pulse" />
      <p className="text-muted-foreground">Cargando...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <NotificationProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard/overview" replace />} />
            <Route path="/auth" element={<Auth />} />

            {/* Dashboard layout stays mounted — only Outlet content changes */}
            <Route element={<DashboardLayoutRoute />}>
              <Route path="/dashboard/overview" element={<Overview />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/movements" element={<Movements />} />
              <Route path="/dashboard/orders" element={<Orders />} />
              <Route path="/dashboard/invoices" element={<Invoices />} />
              <Route path="/dashboard/users" element={<UsersAndClients />} />
              <Route path="/dashboard/clients" element={<UsersAndClients />} />
              <Route path="/dashboard/check-invoices" element={<CheckInvoices />} />
              <Route path="/dashboard/product-mappings" element={<Navigate to="/dashboard/settings" replace />} />
              <Route path="/dashboard/settings" element={<Settings />} />
              <Route path="/dashboard/administration" element={<Administration />} />
              <Route path="/dashboard/administration/clients" element={<ClientsHub />} />
              <Route path="/dashboard/administration/clients/:id" element={<ClientDetail />} />
              <Route path="/dashboard/amazon" element={<Amazon />} />
              <Route path="/dashboard/facturapro" element={<FacturaPro />} />
              <Route path="/dashboard/profile" element={<Profile />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
    </NotificationProvider>
  </QueryClientProvider>
);

export default App;
